<script>
import ContactAPI from 'dashboard/api/contacts';
import CompanyAPI from 'dashboard/api/companies';

const payloadFrom = response =>
  response.data?.payload || response.data?.data?.payload || [];

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'contact',
      // `contact_company` e o sentido inverso de `contact`: o contato esta
      // fixo (painel do contato) e o que se busca e a empresa.
      validator: value =>
        ['contact', 'company', 'contact_company'].includes(value),
    },
    // Nao se aplica ao `contact_company`, onde nao ha empresa de origem.
    companyId: {
      type: [String, Number],
      default: null,
    },
    existingCompanies: {
      type: Array,
      default: () => [],
    },
    existingContacts: {
      type: Array,
      default: () => [],
    },
    relatedCompanies: {
      type: Array,
      default: () => [],
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      query: '',
      results: [],
      selected: null,
      relationshipType: '',
      primary: false,
      isSearching: false,
      searchError: '',
    };
  },
  computed: {
    modalVisibility: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) this.close();
      },
    },
    showsPrimary() {
      return this.type === 'contact' || this.type === 'contact_company';
    },
    primaryLabel() {
      // O campo e unico por contato: do lado do contato ele marca a empresa
      // principal DELE, e nao o "contato principal da empresa".
      return this.$t(
        this.type === 'contact_company'
          ? 'COMPANY.PROFILE.ASSOCIATION.PRIMARY_COMPANY'
          : 'COMPANY.PROFILE.ASSOCIATION.PRIMARY_CONTACT'
      );
    },
    // Marcar outra como principal desmarca a atual (indice unico por contato
    // no backend): avisa antes, em vez de trocar em silencio.
    replacesPrimaryCompany() {
      return (
        this.type === 'contact_company' &&
        this.primary &&
        this.existingCompanies.some(company => company.primary)
      );
    },
    title() {
      return this.$t(
        `COMPANY.PROFILE.ASSOCIATION.${this.type.toUpperCase()}.TITLE`
      );
    },
    placeholder() {
      return this.$t(
        `COMPANY.PROFILE.ASSOCIATION.${this.type.toUpperCase()}.PLACEHOLDER`
      );
    },
    duplicateMessage() {
      if (!this.selected) return '';
      if (this.type === 'contact' && this.isExistingContact(this.selected.id)) {
        return this.$t('COMPANY.PROFILE.ASSOCIATION.DUPLICATE_CONTACT');
      }
      if (
        this.type === 'contact_company' &&
        this.existingCompanies.some(
          company => String(company.id) === String(this.selected.id)
        )
      ) {
        return this.$t('COMPANY.PROFILE.ASSOCIATION.DUPLICATE_COMPANY');
      }
      if (this.type === 'company') {
        if (String(this.selected.id) === String(this.companyId)) {
          return this.$t('COMPANY.PROFILE.ASSOCIATION.SAME_COMPANY');
        }
        if (this.isExistingCompany(this.selected.id)) {
          return this.$t('COMPANY.PROFILE.ASSOCIATION.DUPLICATE_COMPANY');
        }
      }
      return '';
    },
    canSubmit() {
      return this.selected && !this.duplicateMessage && !this.isSubmitting;
    },
  },
  watch: {
    show(value) {
      if (!value) return;
      this.reset();
      this.focusSearch();
    },
    type() {
      this.reset();
    },
  },
  mounted() {
    if (this.show) this.focusSearch();
  },
  methods: {
    // Sem foco, o que se digita logo depois de abrir cai no campo da tela por
    // tras do modal.
    focusSearch() {
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    reset() {
      this.query = '';
      this.results = [];
      this.selected = null;
      this.relationshipType = '';
      this.primary = false;
      this.searchError = '';
    },
    close() {
      this.$emit('close');
    },
    contactFor(association) {
      return association.contact || association;
    },
    relatedCompanyIds(relationship) {
      return [
        relationship.source_company_id,
        relationship.target_company_id,
        relationship.source_company?.id,
        relationship.target_company?.id,
      ]
        .filter(Boolean)
        .map(String);
    },
    isExistingContact(contactId) {
      return this.existingContacts.some(
        association =>
          String(association.contact_id || this.contactFor(association).id) ===
          String(contactId)
      );
    },
    isExistingCompany(companyId) {
      return this.relatedCompanies.some(relationship =>
        this.relatedCompanyIds(relationship).includes(String(companyId))
      );
    },
    itemTitle(item) {
      return item.name || item.email || item.phone_number || `#${item.id}`;
    },
    itemSubtitle(item) {
      if (this.type === 'contact') {
        return item.email || item.phone_number || '';
      }
      return item.website_url || '';
    },
    async search() {
      if (!this.query.trim()) return;

      this.isSearching = true;
      this.searchError = '';
      try {
        let response;
        if (this.type === 'contact') {
          response = await ContactAPI.search(this.query.trim(), 1);
        } else {
          response = await CompanyAPI.search(this.query.trim(), 1);
        }
        this.results = payloadFrom(response);
      } catch (error) {
        this.searchError = this.$t('COMPANY.PROFILE.ASSOCIATION.SEARCH_ERROR');
      } finally {
        this.isSearching = false;
      }
    },
    submit() {
      if (!this.canSubmit) return;

      const basePayload = {
        relationship_type: this.relationshipType.trim() || null,
      };
      if (this.type === 'contact') {
        this.$emit('submit', {
          ...basePayload,
          contact_id: this.selected.id,
          primary: this.primary,
        });
      } else if (this.type === 'contact_company') {
        this.$emit('submit', {
          ...basePayload,
          company_id: this.selected.id,
          primary: this.primary,
        });
      } else {
        this.$emit('submit', {
          ...basePayload,
          target_company_id: this.selected.id,
        });
      }
    },
  },
};
</script>

<template>
  <woot-modal :show.sync="modalVisibility" :on-close="close">
    <woot-modal-header :header-title="title" />
    <div class="flex flex-col gap-4 px-8 pb-8">
      <div class="grid gap-2 md:grid-cols-[1fr_auto]">
        <input
          ref="searchInput"
          v-model.trim="query"
          type="text"
          class="mb-0"
          :placeholder="placeholder"
          @keyup.enter="search"
        />
        <woot-button
          icon="search"
          color-scheme="secondary"
          :is-loading="isSearching"
          @click="search"
        >
          {{ $t('COMPANY.PROFILE.ASSOCIATION.SEARCH') }}
        </woot-button>
      </div>

      <p v-if="searchError" class="m-0 text-sm text-red-600">
        {{ searchError }}
      </p>

      <div class="max-h-64 overflow-y-auto rounded-md border border-line">
        <button
          v-for="item in results"
          :key="item.id"
          type="button"
          class="flex w-full items-start justify-between gap-3 border-b border-line-faint px-3 py-2 text-left last:border-b-0 hover:bg-surface-hover"
          :class="{
            'bg-woot-50 dark:bg-woot-900/20':
              selected && selected.id === item.id,
          }"
          @click="selected = item"
        >
          <span class="min-w-0">
            <span class="block truncate text-sm font-medium text-body">
              {{ itemTitle(item) }}
            </span>
            <span class="block truncate text-xs text-slate-500">
              {{ itemSubtitle(item) }}
            </span>
          </span>
          <fluent-icon
            v-if="selected && selected.id === item.id"
            icon="checkmark"
            size="16"
          />
        </button>
        <p
          v-if="!results.length"
          class="m-0 p-4 text-center text-sm text-slate-500"
        >
          {{ $t('COMPANY.PROFILE.ASSOCIATION.EMPTY_RESULTS') }}
        </p>
      </div>

      <label class="flex flex-col gap-1 text-sm">
        <span>{{ $t('COMPANY.PROFILE.ASSOCIATION.RELATIONSHIP') }}</span>
        <input
          v-model.trim="relationshipType"
          type="text"
          class="mb-0"
          :placeholder="
            $t('COMPANY.PROFILE.ASSOCIATION.RELATIONSHIP_PLACEHOLDER')
          "
        />
      </label>

      <label v-if="showsPrimary" class="flex items-center gap-2 text-sm">
        <input v-model="primary" type="checkbox" class="m-0" />
        <span>{{ primaryLabel }}</span>
      </label>

      <p
        v-if="replacesPrimaryCompany"
        class="m-0 rounded-md bg-amber-50 p-3 text-sm text-amber-800"
      >
        {{ $t('COMPANY.PROFILE.ASSOCIATION.REPLACES_PRIMARY_COMPANY') }}
      </p>

      <p
        v-if="duplicateMessage"
        class="m-0 rounded-md bg-amber-50 p-3 text-sm text-amber-800"
      >
        {{ duplicateMessage }}
      </p>

      <div class="flex justify-end gap-2">
        <woot-button variant="clear" color-scheme="secondary" @click="close">
          {{ $t('COMPANY.FORM.CANCEL') }}
        </woot-button>
        <woot-button
          color-scheme="primary"
          :is-loading="isSubmitting"
          :is-disabled="!canSubmit"
          @click="submit"
        >
          {{ $t('COMPANY.PROFILE.ASSOCIATION.ADD') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>
